<template>
  <div class="relative flex items-center justify-between h-full">
    <font-awesome-icon icon="fa-solid fa-magnifying-glass" class="mr-1.5 text-gray-600 text-sm" />
    <AutoComplete
      v-model="search.storeOrOutletSearchModel"
      :suggestions="storesOrOutletSearch.results"
      :delay="300"
      append-to="self"
      :virtual-scroller-options="{
        itemSize: 40,
        style: {
          overflowY: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box'
        }
      }"
      :loading="storesOrOutletSearch.pending"
      :scroll-height="'250px'"
      :min-length="2"
      @complete="$event => performSearch($event.query)"
      @item-select="onStoreOrOutletsItemSelect"
      :placeholder="t('storeOrOutletName')"
      option-group-label="country"
      option-group-children="stores"
      option-label="name"
      option-value="id"
      :pt="pt"
    >
      <template #empty>
        <span class="block px-4 py-2 text-sm font-medium text-trublue-700">
          {{ t('noResultsFound') }}
        </span>
      </template>
      <template #loadingicon>
        <div class="px-3">
          <div class="loader"></div>
        </div>
      </template>
    </AutoComplete>
    <UButton
      v-show="showClearButton"
      variant="ghost"
      class="relative right-0 -mt-4 text-gray-600 top-2 hover:bg-transparent focus:bg-transparent active:bg-transparent"
      @click="clear"
    >
      <font-awesome-icon icon="fa-solid fa-times" class="text-gray-600" />
    </UButton>
  </div>
</template>

<script setup lang="ts">
import type { StoreOrOutlet } from '~/types'
import type { AutoCompleteItemSelectEvent, AutoCompletePassThroughOptions } from 'primevue/autocomplete'
import type { PassThrough } from 'primevue/ts-helpers'
import { ref, watch, onMounted, nextTick } from 'vue'

const panelRef = ref<HTMLElement | null>(null)
let savedPanelStyle: Partial<CSSStyleDeclaration> = {}

const { isSafari } = useDevice()
const { t } = useI18n()
const geocoded = useGeocodedStore()

const config = useRuntimeConfig()
const map = useMapStore()
const search = useSearchStore()
const storesOrOutletSearch = useStoresOrOutletsStore()
const gtm = useGtm()
const ui = useUiStore()

function onStoreOrOutletsItemSelect({ value }: AutoCompleteItemSelectEvent) {
  const storeOrOutlet = value as StoreOrOutlet
  search.storeOrOutlet = storeOrOutlet
  geocoded.clear()
  search.location = undefined

  if (!search.storeOrOutlet?.isBrand) {
    map.selectedFeatureId = storeOrOutlet.id
    map.navigateTo = [storeOrOutlet.long, storeOrOutlet.lat]
    map.zoom = config.public.map.storeZoomLevel
  } else {
    map.navigateTo = [storeOrOutlet.long, storeOrOutlet.lat]
    map.zoom = config.public.map.stateZoomLevel
  }

  wait(1000, () => {
    if (!search.storeOrOutlet?.isBrand) {
      ui.showMapView()
    } else {
      ui.showListView()
    }
  })
}

function wait(ms: number, cb: () => void) {
  setTimeout(cb, ms)
}

function performSearch(q: string) {
  if (q.length < 2) return
  gtm?.trackEvent({
    event: 'Search by Store or Outlet',
    category: 'search',
    action: 'User searched by Store or Outlet',
    label: q
  })
  storesOrOutletSearch.search(q)
}

function clear() {
  search.storeOrOutletSearchModel = undefined
  search.storeOrOutlet = undefined
  storesOrOutletSearch.reset()
}

onMounted(() => {
  const observer = new MutationObserver(() => {
    const panel = document.querySelector('[data-pc-section="panel"]') as HTMLElement | null
    if (panel) {
      if (Object.keys(savedPanelStyle).length === 0) {
        const computed = getComputedStyle(panel)
        savedPanelStyle = {
          width: computed.width,
          transform: computed.transform,
          top: computed.top,
          right: computed.right
        }
        panelRef.value = panel
      } else {
        panel.style.width = savedPanelStyle.width || ''
        panel.style.transform = savedPanelStyle.transform || ''
        panel.style.top = savedPanelStyle.top || ''
        panel.style.right = savedPanelStyle.right || ''
      }
    }
  })

  observer.observe(document.body, {
    childList: true,
    subtree: true
  })
})

watch(
  () => storesOrOutletSearch.results,
  async () => {
    await nextTick()
    const el = panelRef.value
    if (el && Object.keys(savedPanelStyle).length) {
      el.style.width = savedPanelStyle.width || ''
      el.style.transform = savedPanelStyle.transform || ''
      el.style.top = savedPanelStyle.top || ''
      el.style.right = savedPanelStyle.right || ''
    }
  }
)

const showClearButton = computed(() => storesOrOutletSearch.searchTerm && storesOrOutletSearch.status === 'success')

const pt = computed<PassThrough<AutoCompletePassThroughOptions>>(() => ({
  root: {
    class: 'w-full flex justify-between items-center'
  },
  container: {
    class: 'list-none cursor-text'
  },
  input: {
    class: [
      'h-full text-sm text-gray-600 w-full py-1.5 placeholder-gray-500 bg-transparent outline-none selection:bg-trublue',
      isSafari ? 'text-base' : 'text-sm'
    ]
  },
  panel: ({ props }) => ({
    class: [
      'absolute z-50 !top-[calc(100%+10px)] right-0',
      'bg-white shadow-xl border border-gray-700',
      'max-h-[250px] box-border overflow-hidden',
      'w-[calc(100%+4.5rem)] -translate-x-5',
      props.suggestions?.length === 0 ? 'pb-0' : 'pb-4'
    ]
  }),
  list: {
    class: 'm-0 p-0 list-none overflow-y-auto overflow-x-hidden box-border'
  },
  emptyMessage: {
    class: 'px-4 py-2 text-sm text-trublue-700 font-semibold'
  },
  item: {
    class:
      'px-4 py-2 flex text-gray-500 justify-between cursor-pointer hover:bg-trublue-50 text-sm overflow-hidden relative whitespace-nowrap m-0'
  },
  itemgroup: {
    class: ['m-0 px-4 pt-2 border-gray-700 text-trublue-700 bg-white font-semibold text-sm', 'cursor-auto']
  }
}))
</script>
